


import { computed, defineComponent, onMounted, onUnmounted, ref } from "@vue/composition-api";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import {
  ContentFeed,
  ContentFeedTypeEnum,
  InputFollowingContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useContentFeed from "@/hooks/useContentFeed";
import useMySubscriptionLevels from "@/hooks/useMySubscriptionLevels";
import usePager from "@/hooks/usePager";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { feedFields } from "@/lib/fieldsHelper";
import config from "@/config";
import { EVENTS } from "@/const";
import Announcements from "@/components/Announcements";
import BasePage from "@/components/BasePage";
import ContentFeedList from "@/components/ContentFeedList";
import EmptyContent from "@/components/EmptyContent";
import StoriesLine from "@/components/StoriesLine";

export default defineComponent({
  name: "PageFeed",
  components: { Announcements, ContentFeedList, BasePage, EmptyContent, StoriesLine },
  setup() {
    const { authUserStrict } = useAuthUser();
    const { contentFeedFollowingSearch } = useContentFeed();
    const { getMySubscriptionLevels } = useMySubscriptionLevels();
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.FILESET]);
    const loading = ref(false);

    const {
      enableScroll: scroll,
      data: contentFeedResponse,
      items: contentFeedList,
      updateItem,
      removeItem,
      getFirstPage,
      getNextPage: getNext,
    } = usePager(page => {
      const data: InputFollowingContentFeedSearch = {
        followerId: authUserStrict.value.id,
        types: [ContentFeedTypeEnum.MEDIA, ContentFeedTypeEnum.EVENT, ContentFeedTypeEnum.RICH_TEXT],
        page,
        size: 10,
        sort: "PUBLISH_TIME,DESC",
      };
      const fields = feedFields();
      return contentFeedFollowingSearch(data, undefined, fields);
    });

    const emptyFeed = computed(() => !loading.value && contentFeedList.value.length === 0);
    const showAnnouncements = computed(() => config.featureAnnouncements);

    const onContentFeedUpdate = (contentFeed: ContentFeed) => {
      updateItem(contentFeed, (i1, i2) => i1.id === i2.id);
    };

    const deleteHandler = (feedId: string) => {
      removeItem(i => i.id === feedId);
    };

    const updateFeed = async (payload: { userId: string; feedId?: string }) => {
      if (!contentFeedResponse.value || !contentFeedResponse.value.result.length) {
        return;
      }

      contentFeedResponse.value = {
        ...contentFeedResponse.value,
        result: await getPurchasedContentFeeds(
          { authorId: payload.userId, purchasedFeedId: payload.feedId },
          contentFeedResponse.value,
          undefined,
          feedFields()
        ),
      };
    };

    const getData = async () => {
      loading.value = true;
      try {
        await Promise.all([getFirstPage(), getPaymentPackages(), getMySubscriptionLevels()]);
      } finally {
        loading.value = false;
      }
    };

    getData();

    onMounted(() => {
      emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
    });
    onUnmounted(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
    });

    return {
      loading,
      emptyFeed,
      scroll,
      contentFeedResponse,
      contentFeedList,
      paymentPackages,
      showAnnouncements,
      getNext,
      deleteHandler,
      onContentFeedUpdate,
    };
  },
});
